import { useState } from 'react'
import tw, { css } from 'twin.macro'
import { graphql } from 'gatsby'
import { RenderRichTextData, ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text'
import { useEventListener, useMediaQuery } from 'usehooks-ts'

import RichText from '@/components/ui/rich-text'
import DynamicAction, { DynamicActionProps } from '@/components/ui/dynamic-action'
import { buttonStyles, VariantType } from '@/components/ui/cta-link'
import ImageContentful from '@/components/ui/image-contentful'
import colors from '@/constants/colors'
import useMutationObserver from '@/utils/useMutationObserver'
import { extractDataAttributes } from '@/utils/dom'

export const fragment = graphql`
  fragment SectionCta on ContentfulSectionCta {
    id
    __typename
    actions {
      ... on ContentfulComponentCtaLink {
        ...CtaLink
      }
      ... on ContentfulComponentHubspotCta {
        ...HubspotCTA
      }
    }
    body {
      raw
      # references {
      #   ...Link
      # }
    }
    displayStyle
    sticky
    image {
      ...Asset
    }
    backgroundImage {
      ...Asset
    }
    backgroundColor
  }
`

export type DisplayStyles = 'Inline' | 'Block'

export type SectionCtaProps = {
  className?: string
  actions?: DynamicActionProps[]
  body?: RenderRichTextData<ContentfulRichTextGatsbyReference>
  displayStyle?: DisplayStyles
  sticky?: boolean
  image?: Queries.AssetFragment
  backgroundImage?: Queries.AssetFragment
  backgroundColor?:
    | (typeof colors)['primaryBlue']
    | (typeof colors)['indigoBlue']
    | (typeof colors)['white']
    | (typeof colors)['lightGray']
}

export default function Cta({
  className,
  actions,
  body,
  displayStyle,
  sticky = false,
  image,
  backgroundImage,
  backgroundColor: _backgroundColor,
  ...rest
}: SectionCtaProps): JSX.Element {
  const dataAttributes = extractDataAttributes(rest)
  const backgroundColor = _backgroundColor || colors.primaryBlue

  const renderCTA = (action: DynamicActionProps, btnStyle: VariantType, scrollToOffset = 0) => (
    <DynamicAction
      key={action.id}
      {...action}
      variant={sticky ? btnStyle : action.variant}
      backgroundColor={sticky ? colors.white : backgroundColor}
      scrollToOffset={scrollToOffset}
    />
  )

  const smallMobile = useMediaQuery('(max-width: 40em)')

  const [headerHeight, setHeaderHeight] = useState(0)
  const calcHeaderHeight = () => {
    const header = document.querySelector('header')
    if (header) {
      const height = header.offsetHeight
      setHeaderHeight(height)
    }
  }
  useMutationObserver('header', calcHeaderHeight)
  useEventListener('resize', calcHeaderHeight)

  return (
    <section
      className={className}
      id={sticky ? 'sticky-cta' : undefined}
      css={[tw`max-w-8xl mx-auto`, sticky ? tw`sm:sticky z-20 px-0 lg:px-9 w-fit` : tw`px-5 lg:px-9`]}
      style={sticky && headerHeight ? { top: `${headerHeight + 12}px` } : undefined}
      {...dataAttributes}
    >
      <div
        tw="flex items-center gap-6"
        css={[
          displayStyle === 'Inline' && tw`justify-center content-center flex-col xl:(flex-row)`,
          displayStyle === 'Block' && tw`flex-col`,
          !sticky && backgroundColor === colors.primaryBlue && tw`bg-primaryBlue text-white`,
          !sticky && backgroundColor === colors.indigoBlue && tw`bg-indigoBlue text-white`,
          !sticky && backgroundColor === colors.white && tw`bg-white text-indigoBlue! shadow-lg`,
          !sticky && backgroundColor === colors.lightGray && tw`bg-gray-100 text-indigoBlue!`,
          sticky ? tw`bg-white sm:bg-transparent` : tw`px-5 rounded-4xl py-12`,
          tw`bg-cover bg-center bg-no-repeat`,
        ]}
        style={backgroundImage ? { backgroundImage: `url(${backgroundImage.file.url})` } : undefined}
      >
        {!sticky && image && (
          <div tw="[svg]:(fill-none w-[190px] h-auto)">
            <ImageContentful {...image} />
          </div>
        )}
        {!sticky && body && (
          <div
            tw="text-center [h2,h3,h4]:(h6 m-0)"
            css={[
              css`
                p {
                  ${tw`text-2xl m-0`}
                  text-wrap: balance;
                }
              `,
            ]}
          >
            <RichText body={body} />
          </div>
        )}

        {actions && (
          <div>
            <div
              tw="flex flex-col items-center shrink-0 flex-wrap"
              data-testid="actions"
              css={
                sticky
                  ? tw`gap-0 sm:gap-4 sm:flex-row sm:rounded-full p-3 sm:shadow-even-around bg-white`
                  : tw`gap-4 lg:flex-row`
              }
            >
              {actions.map((action, idx) => (
                <div key={idx}>
                  {!sticky && (
                    <div
                      css={[
                        tw`items-center relative`,
                        idx === 0 &&
                          !sticky &&
                          actions.length > 1 &&
                          tw`lg:(pr-4 after:(content-[''] block right-0 top-1 bottom-1 absolute w-[1px]))`,
                        (backgroundColor === colors.primaryBlue || backgroundColor === colors.indigoBlue) &&
                          tw`after:bg-white`,
                        (backgroundColor === colors.white || backgroundColor === colors.lightGray) &&
                          tw`after:bg-indigoBlue`,
                      ]}
                    >
                      {renderCTA(action, buttonStyles.btnSolid, 200)}
                    </div>
                  )}

                  {sticky && (
                    <>
                      {smallMobile ? (
                        <div tw="block sm:!hidden">{renderCTA(action, buttonStyles.btnArrow, 100)}</div>
                      ) : (
                        <div css={[sticky ? tw`hidden sm:flex` : tw`flex`, tw`items-center relative`]}>
                          {renderCTA(action, buttonStyles.btnSolid, 200)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
